/* Main Store Configuration */

import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { api } from "@/services/api";
import { api as widgetApi } from "@pulse-ui/api-client";

// Import slices
import authReducer from "../features/auth/authSlice";
import alertsReducer from "../features/alerts/alertsSlice";
import navigationReducer from "../features/navigation/navigationSlice";
import preferencesReducer from "../features/preferences/preferencesSlice";
import orgReducer from "../features/org/orgSlice";
import tradingReducer from "../features/trading/tradingSlice";
import uiReducer from "../features/ui/uiSlice";
import groupsReducer from "../features/groups/groupsSlice";

// Persist configurations
const authPersistConfig = {
  key: "auth",
  storage,
  whitelist: ["token", "user", "isAuthenticated"],
  // TODO: Add transforms for encryption
  // transforms: [encryptTransform],
};
const navigationPersistConfig = {
  key: "navigation",
  storage,
  whitelist: [
    "selectedPortfolio",
    "selectedAccount",
    "selectedQuoteAsset",
    "selectedSymbol",
  ],
};
const preferencesPersistConfig = {
  key: "preferences",
  storage,
  whitelist: ["theme", "ag_grid"],
};
const tradingPersistConfig = {
  key: "trading",
  storage,
  whitelist: ["pricingFormat"],
};
const groupsPersistConfig = {
  key: "groups",
  storage,
  whitelist: ["groups"],
};

const persistedAuthReducer = persistReducer(authPersistConfig, authReducer);
const persistedNavigationReducer = persistReducer(
  navigationPersistConfig,
  navigationReducer
);
const persistedPreferencesReducer = persistReducer(
  preferencesPersistConfig,
  preferencesReducer
);
const persistedTradingReducer = persistReducer(
  tradingPersistConfig,
  tradingReducer
);
const persistedGroupsReducer = persistReducer(groupsPersistConfig, groupsReducer);

const store = configureStore({
  reducer: {
    // RTK Query Services
    [api.reducerPath]: api.reducer,
    [widgetApi.reducerPath]: widgetApi.reducer,

    // Redux Toolkit slices
    auth: persistedAuthReducer,
    alerts: alertsReducer,
    navigation: persistedNavigationReducer,
    preferences: persistedPreferencesReducer,
    org: orgReducer,
    trading: persistedTradingReducer,
    ui: uiReducer,
    groups: persistedGroupsReducer,
  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    })
      .concat(api.middleware)
      .concat(widgetApi.middleware),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
